import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle, Loader2, Zap, Maximize, Minimize } from 'lucide-react';
import { playSuccessSound } from '@/lib/sound';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerDisplay() {
  const { paymentId } = useParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const prevStatusRef = useRef(null);

  // Fetch initial payment data
  useEffect(() => {
    if (!paymentId) return;
    const fetchPayment = async () => {
      try {
        const res = await fetch(`${API_URL}/api/display/${paymentId}`);
        if (!res.ok) throw new Error('Payment not found');
        const data = await res.json();
        setPayment(data);
        prevStatusRef.current = data.payment_status;
      } catch {
        setError('Payment not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPayment();
  }, [paymentId]);

  // WebSocket for real-time updates
  useEffect(() => {
    if (!paymentId || !payment || payment.payment_status === 'paid') return;

    const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = API_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/payment/${paymentId}`;

    let reconnectTimeout;
    let ws;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'status') {
            if (data.payment_status === 'paid' && prevStatusRef.current !== 'paid') {
              playSuccessSound();
            }
            prevStatusRef.current = data.payment_status;
            setPayment(prev => prev ? { ...prev, payment_status: data.payment_status } : prev);
          }
        } catch {
          // Ignore parse errors
        }
      };

      ws.onclose = () => {
        // Reconnect after 3s if not paid
        reconnectTimeout = setTimeout(() => {
          if (prevStatusRef.current !== 'paid') connect();
        }, 3000);
      };

      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      clearTimeout(reconnectTimeout);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [paymentId, payment?.payment_status]);

  // Fallback polling (in case WS fails)
  useEffect(() => {
    if (!paymentId || !payment || payment.payment_status === 'paid') return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/display/${paymentId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === 'paid' && prevStatusRef.current !== 'paid') {
            playSuccessSound();
          }
          prevStatusRef.current = data.payment_status;
          setPayment(data);
        }
      } catch {
        // Ignore
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [paymentId, payment?.payment_status]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const formatCurrency = (amount, currency = 'EUR') =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency.toUpperCase() }).format(amount);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error || !payment) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center text-white">
        <div className="text-center">
          <Zap className="w-12 h-12 text-orange-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Payment not found</h1>
          <p className="text-zinc-500">This payment link may have expired.</p>
        </div>
      </div>
    );
  }

  const isPaid = payment.payment_status === 'paid';

  return (
    <div ref={containerRef} className="min-h-screen bg-[#09090b] flex flex-col items-center justify-center p-6 relative" data-testid="customer-display">
      {/* Fullscreen toggle */}
      <button
        onClick={toggleFullscreen}
        className="absolute top-4 right-4 w-10 h-10 bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white transition-colors"
        data-testid="fullscreen-toggle-btn"
        title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
      >
        {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
      </button>

      {/* Store name */}
      <div className="flex items-center gap-2 mb-6">
        <Zap className="w-6 h-6 text-orange-500" />
        <span className="text-lg font-extrabold text-white tracking-tighter" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {payment.store_name || 'EmergencyPOS'}
        </span>
      </div>

      {isPaid ? (
        <div className="text-center" data-testid="display-payment-success">
          <div className="w-24 h-24 mx-auto bg-emerald-500/10 flex items-center justify-center mb-6">
            <CheckCircle className="w-16 h-16 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-extrabold text-emerald-400 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Payment received
          </h1>
          <div className="text-5xl font-extrabold text-white mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {formatCurrency(payment.amount, payment.currency)}
          </div>
          <p className="text-sm text-zinc-500 mt-4">Thank you for your payment</p>
        </div>
      ) : (
        <div className="text-center" data-testid="display-payment-pending">
          {/* Amount */}
          <div className="text-5xl sm:text-6xl font-extrabold text-white mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {formatCurrency(payment.amount, payment.currency)}
          </div>
          <p className="text-zinc-500 text-sm mb-8">Scan the QR code below to pay</p>

          {/* QR Code */}
          {payment.checkout_url && (
            <div className="bg-white p-5 inline-block mb-8">
              <QRCodeSVG
                value={payment.checkout_url}
                size={280}
                level="M"
                includeMargin={false}
              />
            </div>
          )}

          {/* Status indicator */}
          <div className="flex items-center justify-center gap-2 text-zinc-500">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium">Waiting for payment...</span>
          </div>
        </div>
      )}
    </div>
  );
}
